/**
 * Public Client Portal — editorial layout that mirrors the PDF quote.
 * NO auth required — access is gated by HMAC-signed `?t=` token in URL.
 * Never renders cost/GP/rate — only sale price + section descriptions.
 */
import { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CheckCircle2, ShieldCheck, AlertCircle, Printer, Loader2, XCircle, Truck, Hammer,
  PaintBucket, ClipboardCheck, HardHat, Sparkles, ClipboardList, DollarSign, Landmark,
  Copy, Paperclip, Download, Package as PackageIcon, Home,
} from "lucide-react";
import { COLORBOND_COLOURS } from "@/lib/fencing";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  n == null ? "" : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 }).format(n);
const fmtInc = (n) => fmt((n || 0) * 1.10);

// Editorial section-heading component — matches PDF page numbering
const Chapter = ({ n, title, children, id }) => (
  <section id={id} className="space-y-4" data-testid={id ? `portal-chapter-${id}` : undefined}>
    <header className="flex items-baseline gap-4 flex-wrap">
      <span className="font-display font-bold text-3xl sm:text-4xl text-cyan-600 tabular-nums">{n}</span>
      <div>
        <h2 className="font-display font-bold text-2xl sm:text-[26px] text-slate-900 leading-tight tracking-tight">{title}</h2>
      </div>
    </header>
    <hr className="border-slate-200 w-16 border-t-2 border-cyan-500" />
    <div>{children}</div>
  </section>
);

export default function ClientPortalQuote() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const [state, setState] = useState({ loading: true, error: null, quote: null });
  const [termsOpen, setTermsOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [sig, setSig] = useState("");
  const [tickedTerms, setTickedTerms] = useState(false);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    if (!token) { setState({ loading: false, error: "Invalid link — token missing", quote: null }); return; }
    (async () => {
      try {
        const previewFlag = params.get("preview") === "1" ? "&preview=1" : "";
        const { data } = await axios.get(`${API_ROOT}/public/quotes/${id}?t=${token}${previewFlag}`);
        setState({ loading: false, error: null, quote: data });
        setSig(data.customer_name || "");
        axios.get(`${API_ROOT}/public/quotes/${id}/attachments?t=${token}`)
          .then((r) => setAttachments(r.data || []))
          .catch(() => {});
      } catch (e) {
        const code = e?.response?.status;
        const msg = code === 403
          ? "This link has expired or is invalid. Please contact CCFI for a fresh link."
          : code === 404 ? "Quote not found." : "Could not load quote. Please try again shortly.";
        setState({ loading: false, error: msg, quote: null });
      }
    })();
  }, [id, token, params]);

  const q = state.quote;
  const isAccepted = q?.status === "accepted";
  const isDeclined = q?.status === "declined";
  const totalLm = useMemo(() => (q?.sections || []).filter((s) => s.section_type === "fence").reduce((sum, s) => sum + (s.length_m || 0), 0), [q]);

  const [progress, setProgress] = useState(null);
  useEffect(() => {
    if (!token || !isAccepted) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_ROOT}/public/quotes/${id}/progress?t=${token}`);
        if (alive) setProgress(data);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [id, token, isAccepted]);

  const doAccept = async () => {
    if (!tickedTerms) { toast.error("Please tick the T&Cs box"); return; }
    if (!sig || sig.length < 2) { toast.error("Please type your full name as signature"); return; }
    setAccepting(true);
    try {
      const { data } = await axios.post(`${API_ROOT}/public/quotes/${id}/accept?t=${token}`, {
        accepted_terms: true, signature_name: sig,
      });
      toast.success("Quote accepted — thanks!");
      setState((s) => ({ ...s, quote: { ...s.quote, status: "accepted", acceptance: {
        accepted_at: data.accepted_at, signature_name: sig, acceptance_id: data.acceptance_id,
      } } }));
      setAcceptOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Acceptance failed");
    } finally { setAccepting(false); }
  };

  const doDecline = async () => {
    setDeclining(true);
    try {
      const { data } = await axios.post(`${API_ROOT}/public/quotes/${id}/decline?t=${token}`, {
        reason: declineReason || null,
      });
      toast.success("Thanks — we've been notified you're not proceeding.");
      setState((s) => ({ ...s, quote: { ...s.quote, status: "declined", decline: {
        declined_at: data.declined_at, reason: declineReason || null, decline_id: data.decline_id,
      } } }));
      setDeclineOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decline failed");
    } finally { setDeclining(false); }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center">
        <Loader2 className="animate-spin text-cyan-600" size={32} />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center px-4">
        <Card className="rounded-sm max-w-md w-full p-8 text-center">
          <AlertCircle className="mx-auto text-rose-500 mb-3" size={40} />
          <h1 className="font-display text-xl font-bold text-slate-900 mb-2">Cannot open quote</h1>
          <p className="text-sm text-slate-600">{state.error}</p>
          <p className="text-xs text-slate-500 mt-6">
            Central Coast Fencing Industries<br />
            02 4324 1441 · info@ccfencingindustries.com.au
          </p>
        </Card>
      </div>
    );
  }

  const sale = q.sale_price || 0;
  const gst = sale * 0.10;
  const totalInc = sale + gst;
  const deposit = totalInc * 0.5;
  // Iter-71: prefer block-grouped visuals; fall back to the flat `visuals`
  // list for legacy portal payloads.
  const visualBlocks = (q.visual_blocks || []);
  const hasVisuals = visualBlocks.some((b) => (b.photos || []).length > 0)
    || (q.visuals || []).some((v) => v.photo_id);
  const chapterOrder = { scope: "01", visuals: hasVisuals ? "02" : null, invest: hasVisuals ? "03" : "02",
                         next: hasVisuals ? "04" : "03", details: hasVisuals ? "05" : "04" };

  return (
    <div className="min-h-screen bg-[#faf7f2] print:bg-white">
      {/* Slim brand strip */}
      <div className="border-b border-slate-200/70 bg-white/70 backdrop-blur-sm print:border-none print:bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/ccfi-logo.png?v=4" alt="CCFI" className="w-10 h-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-bold text-[13px] tracking-tight text-slate-900">CENTRAL COAST FENCING INDUSTRIES</div>
              <div className="text-[10px] text-slate-500 font-mono tracking-wide">02 4324 1441 · West Gosford NSW</div>
            </div>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            {q.portal_home_url && (
              <Button asChild variant="outline" size="sm"
                className="rounded-sm h-9 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                data-testid="portal-home-link-btn">
                <a href={q.portal_home_url}><Home size={13} className="mr-1.5" /> Portal home</a>
              </Button>
            )}
            <Button onClick={() => window.print()} variant="outline" size="sm"
              className="rounded-sm h-9 border-slate-300 hover:border-slate-400"
              data-testid="portal-print-btn">
              <Printer size={13} className="mr-1.5" /> Print / PDF
            </Button>
          </div>
        </div>
      </div>

      {/* ---------- COVER HERO (mirrors PDF page 1) ---------- */}
      <section className="max-w-5xl mx-auto px-5 sm:px-10 pt-10 sm:pt-14 pb-8">
        <div className="text-[11px] font-bold uppercase tracking-[2px] text-cyan-600 mb-5">Quotation</div>
        <h1 className="font-display font-bold text-slate-900 text-4xl sm:text-6xl leading-none tracking-tight mb-4" data-testid="portal-quote-number">
          {q.quote_number}{(q.revision || 1) >= 2 && <span className="text-slate-400 text-2xl sm:text-4xl font-semibold"> · Rev {q.revision}</span>}
        </h1>
        <hr className="w-24 border-t-2 border-cyan-500 mb-8" />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-10">
          <MetaCell label="Prepared for" bold value={q.customer_name} />
          <MetaCell label="Site" value={q.site_address || "—"} />
          <MetaCell label="Issued" value={new Date(q.created_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })} />
          <MetaCell label="Valid" value={"30 days from issue"} />
        </div>

        {/* Hero total */}
        <div className="bg-white border-b-[3px] border-cyan-500 rounded-sm shadow-sm px-6 sm:px-10 py-8 sm:py-10 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500">Total investment</div>
            <div className="text-[10px] text-slate-400 mt-1">inclusive of 10% GST</div>
          </div>
          <div className="font-display font-bold text-4xl sm:text-6xl text-cyan-600 tabular-nums" data-testid="portal-total">
            {fmtInc(sale)}
          </div>
        </div>

        {/* Payment strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 border-b border-slate-200 mt-0">
          <div className="flex items-baseline justify-between py-4 sm:pr-6 border-r-0 sm:border-r border-slate-200">
            <div className="text-sm text-slate-700"><b>50% deposit</b> on acceptance</div>
            <div className="text-sm font-bold text-slate-900 font-mono">{fmt(deposit)}</div>
          </div>
          <div className="flex items-baseline justify-between py-4 sm:pl-6 border-t border-slate-200 sm:border-t-0">
            <div className="text-sm text-slate-700"><b>Balance</b> within 7 days of practical completion</div>
            <div className="text-sm font-bold text-slate-900 font-mono">{fmt(totalInc - deposit)}</div>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-5 sm:px-10 pb-16 grid gap-12 lg:grid-cols-[1fr_280px]">
        {/* ------ Main column ------ */}
        <div className="space-y-12 min-w-0">
          {/* State banners */}
          {isAccepted && (
            <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-4 flex items-start gap-3">
              <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={22} />
              <div>
                <div className="font-semibold text-emerald-900">Quote accepted — thank you!</div>
                <div className="text-xs text-emerald-800 mt-1">
                  Accepted on <b>{new Date(q.acceptance?.accepted_at).toLocaleString("en-AU")}</b> by <b>{q.acceptance?.signature_name}</b>.<br />
                  Reference: <span className="font-mono">{q.acceptance?.acceptance_id}</span>.
                </div>
              </div>
            </div>
          )}
          {isDeclined && (
            <div className="border border-rose-300 bg-rose-50 rounded-sm p-4 flex items-start gap-3">
              <XCircle className="text-rose-600 shrink-0 mt-0.5" size={22} />
              <div>
                <div className="font-semibold text-rose-900">Quote declined</div>
                <div className="text-xs text-rose-800 mt-1">
                  Declined on <b>{new Date(q.decline?.declined_at).toLocaleString("en-AU")}</b>.
                  Reference: <span className="font-mono">{q.decline?.decline_id}</span>.
                  {q.decline?.reason && <><br />Reason: <i>&ldquo;{q.decline.reason}&rdquo;</i></>}
                </div>
              </div>
            </div>
          )}

          {isAccepted && progress?.payment && <PaymentProgressCard payment={progress.payment} />}
          {isAccepted && progress?.payment && !progress.payment.balance_paid_at && (
            <BankTransferCard payment={progress.payment} quoteId={id} token={token} />
          )}
          {isAccepted && progress?.job_exists && progress?.stages && <JobProgressCard progress={progress} />}

          {/* 01 · Scope of works */}
          <Chapter n="01" title="Scope of works" id="scope">
            <p className="text-[15px] text-slate-700 leading-[1.75] whitespace-pre-wrap">
              {q.scope_summary || (() => {
                // Derive the fallback from actual sections rather than the
                // legacy quote-level fence_type default so we never mislabel a
                // chain-wire quote as "Colorbond".
                const secFts = Array.from(new Set(
                  (q.sections || [])
                    .filter((s) => s.section_type === "fence" && s.fence_type)
                    .map((s) => s.fence_type.replace(/_/g, " "))
                ));
                const label = secFts.length ? secFts.join(" + ") : (q.fence_type || "").replace(/_/g, " ") || "fencing";
                return `Supply and installation of ${label}, as discussed on site.`;
              })()}
            </p>
            {(totalLm > 0 || q.fence_type) && (() => {
              const primaryTypes = Array.from(new Set(
                (q.sections || [])
                  .filter((s) => s.section_type === "fence" && s.fence_type)
                  .map((s) => s.fence_type)
              ));
              const primary = primaryTypes[0] || q.fence_type;
              const primaryLabel = primaryTypes.length > 1
                ? "Multiple types"
                : (primary || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
              return (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mt-7 border-t border-slate-200 pt-5">
                {totalLm > 0 && <MetaCell label="Fence length" value={<span className="font-mono">{totalLm.toFixed(1)} m</span>} bold />}
                {primary && primary !== "other" && (
                  <MetaCell label="Primary type" value={primaryLabel} bold />
                )}
                {(q.lines || []).length > 0 ? (
                  <MetaCell label="Line items" value={<span className="font-mono">{q.lines.length}</span>} bold />
                ) : (q.sections || []).length > 0 && (
                  <MetaCell label="Sections" value={<span className="font-mono">{q.sections.length}</span>} bold />
                )}
              </div>
              );
            })()}

            {(q.sections || []).length > 0 && (q.lines || []).length === 0 && (
              <div className="mt-8 space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500 mb-3">Includes</div>
                {q.sections.map((s) => <PortalSectionRow key={s.id} s={s} />)}
              </div>
            )}

            {/* Iter-70: Line Item -> Section hierarchy. This mirrors the PDF: each
                line item shows a customer-facing title + scope narrative; the
                internal section breakdown is only shown when the estimator ticked
                "Show sub-section breakdown to customer" on that line. */}
            {(q.lines || []).length > 0 && (
              <div className="mt-8 space-y-6">
                {q.lines.map((ln, idx) => (
                  <div key={ln.id || idx} className="border-t border-slate-200 pt-5">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="font-mono text-[10px] tracking-[1.6px] text-cyan-600">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <h3 className="text-[17px] font-semibold text-slate-900 tracking-tight">{ln.title || "Line item"}</h3>
                    </div>
                    {ln.scope_narrative && (
                      <p className="text-[14.5px] text-slate-700 leading-[1.7] whitespace-pre-wrap ml-8">
                        {ln.scope_narrative}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Chapter>

          {/* 02 · Visuals — Iter-71: grouped per design block. Each block heading
              shows the fence style; each photo pair inside shows Before + up to
              three AI-generated After options. Legacy quotes without blocks fall
              back to the flat `visuals` gallery. */}
          {hasVisuals && (
            <Chapter n={chapterOrder.visuals} title="Your site — before & after" id="visuals">
              <p className="text-xs text-slate-500 leading-relaxed mb-6 max-w-2xl">
                AI-assisted concept previews of your chosen designs and colours. Final product is fabricated to the specifications above.
              </p>
              <div className="space-y-10">
                {visualBlocks.length > 0
                  ? visualBlocks.map((b) => (
                      <PortalVisualBlock
                        key={b.id}
                        block={b}
                        quoteId={id}
                        token={token}
                      />
                    ))
                  : (q.visuals || []).map((v) => (
                    <div key={v.photo_id} className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <figure className="space-y-1.5">
                          <figcaption className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500">Pre-existing</figcaption>
                          <img src={`${API_ROOT}/public/quotes/${id}/visual-file/${v.photo_id}?t=${token}`}
                            alt="Current site" loading="lazy"
                            className="w-full aspect-[4/3] object-cover rounded-sm border border-slate-200"
                            data-testid={`portal-before-${v.photo_id}`} />
                        </figure>
                        {(v.renders || []).slice(0, 1).map((r) => (
                          <figure key={r.id} className="space-y-1.5">
                            <figcaption className="text-[10px] font-bold uppercase tracking-[1.6px] text-cyan-600 truncate">
                              Proposed — {(r.design || "").replace(/_/g, " ")} · {r.colour_name}
                            </figcaption>
                            <img src={`${API_ROOT}/public/quotes/${id}/visual-file/${r.id}?t=${token}`}
                              alt="Proposed fence" loading="lazy"
                              className="w-full aspect-[4/3] object-cover rounded-sm border border-cyan-300"
                              data-testid={`portal-after-${r.id}`} />
                          </figure>
                        ))}
                      </div>
                      {(v.renders || []).length > 1 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                          {v.renders.slice(1, 4).map((r) => (
                            <figure key={r.id} className="space-y-1.5">
                              <figcaption className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500 truncate">
                                Also considered — {r.colour_name}
                              </figcaption>
                              <img src={`${API_ROOT}/public/quotes/${id}/visual-file/${r.id}?t=${token}`}
                                alt="Alternative option" loading="lazy"
                                className="w-full aspect-[4/3] object-cover rounded-sm border border-slate-200" />
                            </figure>
                          ))}
                        </div>
                      )}
                      {v.caption && <div className="text-xs text-slate-500 italic">{v.caption}</div>}
                    </div>
                  ))}
              </div>
            </Chapter>
          )}

          {/* 03 · Investment */}
          <Chapter n={chapterOrder.invest} title="Investment" id="invest">
            <div className="divide-y divide-slate-200 border-y border-slate-200">
              <InvestRow label="Price for the works described" value={fmt(sale)} />
              <InvestRow label="GST (10%)" value={fmt(gst)} />
            </div>
            <div className="flex items-baseline justify-between border-y-2 border-cyan-500 mt-0 py-5">
              <div className="font-semibold text-slate-900">Total (inc GST)</div>
              <div className="font-display font-bold text-3xl text-cyan-600 tabular-nums">{fmt(totalInc)}</div>
            </div>

            <h3 className="font-semibold text-slate-900 mt-9 mb-3">Payment schedule</h3>
            <div className="border border-slate-200 rounded-sm overflow-hidden">
              <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[130px_1fr_120px] bg-[#faf7f2] px-4 py-2.5 text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500 border-b-2 border-cyan-500 gap-3">
                <div>Stage</div>
                <div className="hidden sm:block">When</div>
                <div className="text-right">Amount</div>
              </div>
              <PayRow stage="Deposit" when="On acceptance of this quote" amount={fmt(deposit)} />
              <PayRow stage="Balance" when="Within 7 days of practical completion" amount={fmt(totalInc - deposit)} last />
            </div>
          </Chapter>

          {/* 04 · How this becomes a job */}
          <Chapter n={chapterOrder.next} title="How this becomes a job" id="steps">
            <ol className="divide-y divide-slate-200 border-y border-slate-200">
              <StepRow n="01" title="Accept online" body="Review, accept and sign this quote in a single click below." />
              <StepRow n="02" title="Pay 50% deposit" body="We hold your slot in the fabrication schedule and order material." />
              <StepRow n="03" title="Fabrication & install" body="Track progress live on this page — schedule confirmation, dispatch, install day." />
              <StepRow n="04" title="Handover & balance" body="Practical-completion walkthrough. Balance payable within 7 days. 12-month workmanship warranty." />
            </ol>
          </Chapter>

          {/* Attachments — kept but restyled */}
          {attachments.length > 0 && (
            <Chapter n={chapterOrder.details} title="Brochures & documents" id="attachments">
              <div className="grid gap-2" data-testid="portal-attachments-card">
                {attachments.map((f) => {
                  const swatch = f.design_colour_hex || COLORBOND_COLOURS.find((c) => c.name === f.design_colour)?.hex;
                  return (
                    <a key={f.id}
                      href={`${API_ROOT}/public/quotes/${id}/attachments/${f.id}/download?t=${token}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-between gap-3 border border-slate-200 rounded-sm px-4 py-3 hover:border-cyan-400 hover:bg-white transition-colors group"
                      data-testid={`portal-attachment-${f.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Paperclip size={16} className="text-cyan-600 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{f.label || f.filename}</div>
                          <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5 flex-wrap">
                            <span className="uppercase tracking-wider">{f.kind}</span>
                            {f.design_colour && (
                              <span className="inline-flex items-center gap-1">
                                <span className="w-3 h-3 rounded-sm border border-slate-300" style={{ backgroundColor: swatch }} />
                                {f.design_colour}
                              </span>
                            )}
                            {f.design_height_mm ? <span>· Height {f.design_height_mm} mm</span> : null}
                          </div>
                        </div>
                      </div>
                      <Download size={15} className="text-slate-400 group-hover:text-cyan-600 shrink-0" />
                    </a>
                  );
                })}
              </div>
            </Chapter>
          )}

          {/* Accept CTA at the bottom (in-flow) */}
          {!isAccepted && !isDeclined && (
            <section className="border-t-2 border-cyan-500 pt-8 mt-6" data-testid="portal-accept-cta">
              <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-cyan-600 mb-2">Ready to proceed?</div>
              <h3 className="font-display font-bold text-2xl text-slate-900 mb-2">Accept &amp; lock in your install date</h3>
              <p className="text-sm text-slate-600 leading-relaxed max-w-2xl mb-5">
                One click formally approves this quote. We&apos;ll invoice the deposit and reach out to schedule your install.{" "}
                <button onClick={() => setTermsOpen(true)} className="text-cyan-600 hover:underline font-medium" data-testid="portal-view-terms-btn">
                  Review terms
                </button>.
              </p>
              <div className="flex flex-wrap gap-3 print:hidden">
                <Button data-testid="portal-open-accept-btn" onClick={() => setAcceptOpen(true)}
                  className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white h-12 px-8 font-semibold text-[15px] shadow-sm">
                  <CheckCircle2 size={17} className="mr-2" /> Accept quote
                </Button>
                <Button data-testid="portal-open-decline-btn" onClick={() => setDeclineOpen(true)} variant="outline"
                  className="rounded-sm h-12 px-6 border-slate-300 hover:border-rose-400 hover:text-rose-700 hover:bg-rose-50">
                  <XCircle size={15} className="mr-2" /> Decline
                </Button>
              </div>
            </section>
          )}
        </div>

        {/* ------ Sticky sidebar (desktop only) ------ */}
        <aside className="hidden lg:block print:hidden">
          <div className="sticky top-6 space-y-4">
            <Card className="rounded-sm border-slate-200 bg-white overflow-hidden shadow-sm">
              <div className="p-5 border-b border-slate-100">
                <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500">Total</div>
                <div className="font-display font-bold text-3xl text-cyan-600 tabular-nums mt-1">{fmtInc(sale)}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">inc GST</div>
              </div>
              <div className="p-5 text-xs text-slate-600 space-y-1.5">
                <div className="flex justify-between"><span>Ex GST</span><span className="font-mono">{fmt(sale)}</span></div>
                <div className="flex justify-between"><span>GST</span><span className="font-mono">{fmt(gst)}</span></div>
                <div className="flex justify-between pt-1.5 border-t border-slate-100"><span>Deposit</span><span className="font-mono">{fmt(deposit)}</span></div>
                <div className="flex justify-between"><span>Balance</span><span className="font-mono">{fmt(totalInc - deposit)}</span></div>
              </div>
              {!isAccepted && !isDeclined && (
                <div className="p-5 pt-0">
                  <Button onClick={() => setAcceptOpen(true)}
                    className="w-full rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white h-11 font-semibold"
                    data-testid="portal-sidebar-accept-btn">
                    <ShieldCheck size={15} className="mr-1.5" /> Accept quote
                  </Button>
                </div>
              )}
            </Card>
            <div className="text-[11px] text-slate-500 leading-relaxed">
              Questions? Reply to the email or call{" "}
              <a href="tel:0243241441" className="text-cyan-600 hover:underline font-medium">02 4324 1441</a>.
            </div>
          </div>
        </aside>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white/70 print:border-none print:bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 py-5 text-[11px] text-slate-500 leading-relaxed">
          <p className="font-mono">Central Coast Fencing Industries · ABN — · 8 Dignity Cres, West Gosford NSW 2250 · 02 4324 1441</p>
          <p className="mt-1">This link is unique to you. Please do not forward.</p>
        </div>
      </footer>

      {/* Terms dialog */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="portal-terms-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Terms &amp; Conditions</DialogTitle>
          </DialogHeader>
          <pre className="whitespace-pre-wrap font-sans text-xs text-slate-700 leading-relaxed">{q?.terms || ""}</pre>
          <div className="text-[10px] text-slate-400 pt-2 font-mono">Version {q?.terms_version}</div>
        </DialogContent>
      </Dialog>

      {/* Accept dialog */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="portal-accept-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Accept quote {q?.quote_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500">Your full name (electronic signature)</Label>
              <Input data-testid="portal-signature-input" value={sig} onChange={(e) => setSig(e.target.value)}
                placeholder="e.g. Robert John Smith" className="mt-1 h-11 rounded-sm" />
              <div className="text-[10px] text-slate-500 mt-1">Type your name exactly as it appears on the quote.</div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-800">
              <input type="checkbox" data-testid="portal-tick-terms" checked={tickedTerms}
                onChange={(e) => setTickedTerms(e.target.checked)} className="mt-1 w-4 h-4 accent-cyan-600" />
              <span>I have read and accept the <button type="button" className="text-cyan-600 underline" onClick={() => setTermsOpen(true)}>Terms &amp; Conditions</button> and authorise CCFI to proceed with this work.</span>
            </label>
            <div className="text-[10px] text-slate-500 leading-relaxed border-t border-slate-100 pt-3">
              Your acceptance is legally binding and records your name, IP address, timestamp, and T&amp;Cs version. A 50% deposit invoice will be raised on acceptance; balance due within 7 days of practical completion.
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => setAcceptOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="portal-confirm-accept-btn" onClick={doAccept}
              disabled={accepting || !tickedTerms || sig.length < 2}
              className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
              {accepting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
              Confirm acceptance
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="portal-decline-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Decline quote {q?.quote_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">
              Sorry to hear you&apos;re not proceeding. Would you mind letting us know why? It helps us improve.
            </p>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500">Reason (optional)</Label>
              <Textarea data-testid="portal-decline-reason" value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="e.g. Went with another quote / project on hold / out of budget"
                rows={3} className="mt-1 rounded-sm text-sm" />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => setDeclineOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="portal-confirm-decline-btn" onClick={doDecline} disabled={declining} variant="outline"
              className="rounded-sm border-rose-400 text-rose-700 hover:bg-rose-50">
              {declining ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <XCircle size={14} className="mr-1.5" />}
              Decline quote
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------- Small view helpers -------------
const MetaCell = ({ label, value, bold }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500 mb-1">{label}</div>
    <div className={`text-[13px] text-slate-800 leading-snug ${bold ? "font-semibold" : ""}`}>{value}</div>
  </div>
);
const InvestRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between py-4">
    <div className="text-sm text-slate-700">{label}</div>
    <div className="font-mono font-semibold text-slate-900">{value}</div>
  </div>
);
const PayRow = ({ stage, when, amount, last }) => (
  <div className={`grid grid-cols-[1fr_auto] sm:grid-cols-[130px_1fr_120px] gap-3 px-4 py-4 items-center ${last ? "" : "border-b border-slate-200"}`}>
    <div className="font-semibold text-slate-900 text-sm">{stage}</div>
    <div className="hidden sm:block text-sm text-slate-600">{when}</div>
    <div className="text-right font-mono font-semibold text-slate-900 text-sm">{amount}</div>
    <div className="col-span-2 sm:hidden text-xs text-slate-500 -mt-2">{when}</div>
  </div>
);
const StepRow = ({ n, title, body }) => (
  <li className="grid grid-cols-[40px_1fr] gap-4 py-4 items-start">
    <div className="font-display font-bold text-cyan-600 text-lg tabular-nums">{n}</div>
    <div>
      <div className="font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{body}</div>
    </div>
  </li>
);

function PortalSectionRow({ s }) {
  const kind = s.section_type === "gate" ? "Gate" : s.section_type === "extra" ? "Extra" : s.section_type === "labour" ? "Labour" : "Fence";
  const colour = s.colour_hex || COLORBOND_COLOURS.find((c) => c.name === s.colour)?.hex;
  return (
    <div className="grid grid-cols-[80px_1fr] gap-4 py-3 border-b border-slate-100">
      <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-cyan-600 pt-0.5">{kind}</div>
      <div>
        <div className="text-sm font-semibold text-slate-900 leading-snug">{s.label}</div>
        <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
          {s.height_mm && <span>Height <b className="font-mono">{s.height_mm} mm</b></span>}
          {s.length_m > 0 && s.section_type === "fence" && <span>Length <b className="font-mono">{s.length_m} m</b></span>}
          {s.qty > 1 && <span>Qty <b className="font-mono">{s.qty}</b></span>}
          {s.colour && (
            <span className="inline-flex items-center gap-1.5">
              Colour <span className="w-3 h-3 rounded-sm border border-slate-300" style={{ backgroundColor: colour }} />
              <b>{s.colour}</b>
            </span>
          )}
        </div>
        {s.notes && <div className="text-[11px] text-slate-500 italic mt-1.5">{s.notes}</div>}
      </div>
    </div>
  );
}

// Iter-71: block-grouped before/after gallery for the client portal. Each
// block heading identifies the fence style; the photos inside show Before +
// up to three After options (customer only sees photos with generated previews).
function PortalVisualBlock({ block, quoteId, token }) {
  const photos = block.photos || [];
  if (photos.length === 0) return null;
  return (
    <section className="space-y-4" data-testid={`portal-visual-block-${block.id}`}>
      <header className="flex items-baseline gap-3 border-b border-slate-200 pb-2">
        <span className="font-mono text-[10px] tracking-[1.6px] text-cyan-600">DESIGN</span>
        <h3 className="text-[16px] font-semibold text-slate-900 tracking-tight">{block.title}</h3>
        {(block.design_label || block.colour_name) && (
          <span className="text-[10px] uppercase tracking-[1.4px] text-slate-500 ml-auto">
            {[block.design_label, block.colour_name].filter(Boolean).join(" · ")}
          </span>
        )}
      </header>
      <div className="space-y-8">
        {photos.map((p) => (
          <div key={p.id} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <figure className="space-y-1.5">
                <figcaption className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500">Pre-existing</figcaption>
                <img src={`${API_ROOT}/public/quotes/${quoteId}/visual-file/${p.id}?t=${token}`}
                  alt="Current site" loading="lazy"
                  className="w-full aspect-[4/3] object-cover rounded-sm border border-slate-200"
                  data-testid={`portal-before-${p.id}`} />
              </figure>
              {(p.renders || []).slice(0, 1).map((r) => (
                <figure key={r.id} className="space-y-1.5">
                  <figcaption className="text-[10px] font-bold uppercase tracking-[1.6px] text-cyan-600 truncate">
                    Proposed — {(r.design || "").replace(/_/g, " ")} · {r.colour_name}
                  </figcaption>
                  <img src={`${API_ROOT}/public/quotes/${quoteId}/visual-file/${r.id}?t=${token}`}
                    alt="Proposed fence" loading="lazy"
                    className="w-full aspect-[4/3] object-cover rounded-sm border border-cyan-300"
                    data-testid={`portal-after-${r.id}`} />
                </figure>
              ))}
            </div>
            {(p.renders || []).length > 1 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                {p.renders.slice(1, 4).map((r) => (
                  <figure key={r.id} className="space-y-1.5">
                    <figcaption className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500 truncate">
                      Also considered — {r.colour_name}
                    </figcaption>
                    <img src={`${API_ROOT}/public/quotes/${quoteId}/visual-file/${r.id}?t=${token}`}
                      alt="Alternative option" loading="lazy"
                      className="w-full aspect-[4/3] object-cover rounded-sm border border-slate-200" />
                  </figure>
                ))}
              </div>
            )}
            {p.caption && <div className="text-xs text-slate-500 italic">{p.caption}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

// ------------- Progress / Bank / Payment helpers (kept, style tightened) -------------
const STAGE_ICONS = {
  deposit_received:    DollarSign,
  scheduled:           ClipboardList,
  materials_ordered:   PackageIcon,
  in_progress:         HardHat,
  completed:           ClipboardCheck,
  whs_plan:            ClipboardList,
  materials_delivered: Truck,
  in_fabrication:      Hammer,
  powder_coat:         PaintBucket,
  installation:        HardHat,
  site_cleanup:        Sparkles,
  final_inspection:    ClipboardCheck,
};

function JobProgressCard({ progress }) {
  const stages = progress.stages || [];
  const pct = progress.progress_pct || 0;
  const curKey = progress.current_stage?.key;
  return (
    <Card className="rounded-sm p-6 border-slate-200 shadow-sm" data-testid="portal-progress-card">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-cyan-600">Job progress</div>
          <div className="text-xs text-slate-500 mt-1">
            {pct >= 100 ? "Completed" : progress.current_stage ? <>Current stage: <b className="text-slate-800">{progress.current_stage.label}</b></> : "Awaiting kickoff"}
          </div>
          {progress.scheduled_start && (
            <div className="text-xs text-cyan-700 font-semibold mt-1" data-testid="portal-progress-dates">
              Install scheduled: {new Date(progress.scheduled_start).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
              {" → "}
              {new Date(progress.scheduled_end || progress.scheduled_start).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="font-display font-bold text-3xl text-cyan-600 tabular-nums">{pct.toFixed(0)}%</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Complete</div>
        </div>
      </div>
      <div className="h-2 bg-slate-100 rounded-sm overflow-hidden mb-5">
        <div className="h-full bg-cyan-500 transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <ol className="space-y-2">
        {stages.map((s) => {
          const done = s.status === "complete";
          const current = curKey === s.key;
          const Icon = STAGE_ICONS[s.key] || CheckCircle2;
          return (
            <li key={s.key} className={`flex items-start gap-3 p-2.5 rounded-sm border ${done ? "bg-emerald-50 border-emerald-200" : current ? "bg-cyan-50 border-cyan-300" : "bg-white border-slate-200"}`}>
              <div className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-sm ${done ? "bg-emerald-600 text-white" : current ? "bg-cyan-600 text-white animate-pulse" : "bg-slate-100 text-slate-400"}`}>
                {done ? <CheckCircle2 size={16} /> : <Icon size={15} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-sm font-medium text-slate-900">
                    <span className="text-[10px] text-slate-400 mr-1.5 font-mono">{String(s.order).padStart(2, "0")}</span>
                    {s.label}
                  </div>
                  {done && s.completed_at && <span className="text-[10px] text-emerald-700 font-mono shrink-0">{new Date(s.completed_at).toLocaleDateString("en-AU")}</span>}
                  {current && !done && <span className="text-[10px] text-cyan-700 font-semibold shrink-0 uppercase tracking-wider">In progress</span>}
                </div>
                {(s.photos || []).length > 0 && (
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    {s.photos.slice(0, 6).map((p, i) => (
                      <a key={p.url} href={p.url} target="_blank" rel="noreferrer" className="block w-14 h-14 rounded-sm overflow-hidden border border-slate-200 shrink-0" title={p.caption || ""}>
                        <img src={p.url} alt={p.caption || `stage photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                      </a>
                    ))}
                    {s.photos.length > 6 && <div className="text-[10px] text-slate-500 self-center">+{s.photos.length - 6} more</div>}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {(progress.photos || []).length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Progress photos from site</div>
          <div className="flex gap-1.5 flex-wrap" data-testid="portal-progress-photos">
            {progress.photos.slice(0, 8).map((p, i) => (
              <a key={p.url} href={p.url} target="_blank" rel="noreferrer" className="block w-14 h-14 rounded-sm overflow-hidden border border-slate-200 shrink-0" title={p.caption || p.stage || ""}>
                <img src={p.url} alt={p.caption || `site photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </a>
            ))}
            {progress.photos.length > 8 && <div className="text-[10px] text-slate-500 self-center">+{progress.photos.length - 8} more</div>}
          </div>
        </div>
      )}
    </Card>
  );
}

function BankTransferCard({ payment, quoteId, token }) {
  const bank = payment.bank || {};
  const ref = payment.reference || {};
  const depositPaid = !!payment.deposit_paid_at;
  const due = depositPaid
    ? { type: "balance", label: "Final balance", amount: payment.balance_amount || 0 }
    : { type: "deposit", label: "50% deposit", amount: payment.deposit_amount || 0 };
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [notified, setNotified] = useState(false);
  const reference = ref.customer_number || ref.quote_number || "";
  const notify = async () => {
    setSending(true);
    try {
      await axios.post(`${API_ROOT}/public/quotes/${quoteId}/payment-notify?t=${token}`, { payment_type: due.type });
      setNotified(true);
      toast.success("Thanks — our team has been notified and will confirm receipt.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not notify us — please call 02 4324 1441");
    } finally { setSending(false); setConfirming(false); }
  };
  const copy = (v) => { try { navigator.clipboard.writeText(v); toast.success("Copied"); } catch { /* older browsers */ } };
  return (
    <Card className="rounded-sm p-6 border-emerald-200 shadow-sm" data-testid="portal-bank-card">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-emerald-700 flex items-center gap-1.5"><Landmark size={12} /> Pay by bank transfer</div>
        <div className="text-right">
          <div className="font-display font-bold text-2xl text-emerald-700 tabular-nums" data-testid="portal-amount-due">{fmt(due.amount)}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">{due.label} due</div>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Direct deposit only — we don&apos;t take card payments. Please use the reference so we can match your payment.</p>
      <div className="grid sm:grid-cols-2 gap-2 text-sm">
        <div className="border border-slate-200 bg-slate-50 rounded-sm p-3">
          <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500">Account name</div>
          <div className="font-semibold text-slate-900 mt-0.5" data-testid="portal-bank-name">{bank.account_name}</div>
        </div>
        <div className="border border-slate-200 bg-slate-50 rounded-sm p-3 flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-slate-500">BSB &amp; Account</div>
            <div className="font-mono font-semibold text-slate-900 mt-0.5" data-testid="portal-bank-numbers">{bank.bsb} · {bank.account_number}</div>
          </div>
          <button onClick={() => copy(`BSB ${bank.bsb} ACC ${bank.account_number}`)} className="text-slate-400 hover:text-emerald-600 print:hidden" title="Copy" data-testid="portal-copy-bank-btn"><Copy size={15} /></button>
        </div>
        <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-3 sm:col-span-2 flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-emerald-700">Payment reference (important)</div>
            <div className="font-mono font-bold text-emerald-900 text-lg mt-0.5" data-testid="portal-payment-reference">{reference}</div>
            {ref.customer_number && ref.quote_number && <div className="text-[10px] text-emerald-700">Your customer number — or use quote {ref.quote_number}</div>}
          </div>
          <button onClick={() => copy(reference)} className="text-emerald-500 hover:text-emerald-700 print:hidden" title="Copy reference" data-testid="portal-copy-ref-btn"><Copy size={15} /></button>
        </div>
      </div>
      <div className="mt-4 print:hidden">
        {notified ? (
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm p-3 flex items-center gap-2" data-testid="portal-payment-notified">
            <CheckCircle2 size={16} className="text-emerald-600" /> Thanks — we&apos;ve been notified and will confirm once the funds land.
          </div>
        ) : confirming ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm p-3">
            <span className="text-sm text-slate-700 flex-1">Confirm you&apos;ve transferred the <b>{due.label.toLowerCase()}</b> of <b>{fmt(due.amount)}</b>?</span>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => setConfirming(false)} className="rounded-sm">Cancel</Button>
              <Button size="sm" data-testid="portal-confirm-paid-btn" onClick={notify} disabled={sending} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
                {sending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <CheckCircle2 size={13} className="mr-1.5" />} Yes — notify CCFI
              </Button>
            </div>
          </div>
        ) : (
          <Button data-testid="portal-paid-btn" onClick={() => setConfirming(true)} variant="outline" className="rounded-sm h-10 border-emerald-400 text-emerald-700 hover:bg-emerald-50">
            <CheckCircle2 size={15} className="mr-1.5" /> I&apos;ve made the transfer
          </Button>
        )}
      </div>
    </Card>
  );
}

function PaymentProgressCard({ payment }) {
  const total = payment.quote_total || 0;
  const depositPaid = !!payment.deposit_paid_at;
  const balancePaid = !!payment.balance_paid_at;
  const deposit = payment.deposit_amount || 0;
  const balance = payment.balance_amount || 0;
  const paid = (depositPaid ? deposit : 0) + (balancePaid ? balance : 0);
  const pct = total > 0 ? (paid / total) * 100 : 0;
  return (
    <Card className="rounded-sm p-6 border-slate-200 shadow-sm" data-testid="portal-payment-card">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[1.6px] text-cyan-600 flex items-center gap-1.5"><DollarSign size={12} /> Payments</div>
          <div className="text-xs text-slate-500 mt-1">
            {balancePaid ? "Paid in full — thank you!" : depositPaid ? `Deposit received · balance ${fmt(balance)} due on completion` : `50% deposit ${fmt(deposit)} required to commence work`}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display font-bold text-2xl text-slate-900 tabular-nums">{fmt(paid)}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">of {fmt(total)}</div>
        </div>
      </div>
      <div className="h-3 bg-slate-100 rounded-sm overflow-hidden flex">
        <div className={`h-full transition-all ${depositPaid ? "bg-emerald-500" : "bg-slate-200"}`} style={{ width: total > 0 ? `${(deposit / total) * 100}%` : "0%" }} />
        <div className={`h-full transition-all ${balancePaid ? "bg-emerald-500" : "bg-slate-200"}`} style={{ width: total > 0 ? `${(balance / total) * 100}%` : "0%" }} />
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 mt-2 font-mono">
        <span className={depositPaid ? "text-emerald-700 font-semibold" : ""}>{depositPaid ? "✓" : "○"} Deposit {fmt(deposit)}</span>
        <span className={balancePaid ? "text-emerald-700 font-semibold" : ""}>{balancePaid ? "✓" : "○"} Balance {fmt(balance)}</span>
      </div>
      <div className="text-[10px] text-slate-500 mt-2">Total quote: {fmt(total)} ex GST · Progress: {pct.toFixed(0)}%</div>
    </Card>
  );
}
